<template>
  <link href="https://unpkg.com/uppy@1.2.0/dist/uppy.min.css" rel="stylesheet">
</template>

<script>
import Uppy from '@uppy/core'
import Dashboard from '@uppy/dashboard'
import Webcam from '@uppy/webcam'
import AwsS3Multipart from '@uppy/aws-s3-multipart'

import Raven from 'raven-js'

import store from '../store.js'
import axios from 'axios'

import { S3_BUCKET_NAME } from '../settings'

export default {
  name: 'Uppy',
  props: {
    withProgress: {
      type: Boolean,
      default: true,
    },
  },
  data () {
    return {
      images: [],
      bytesUploaded: 0,
      bytesTotal: 0,
      showProgress: false,
      uppy: null,
      startTime: null,
      elapsedTime: 0,
    }
  },
  methods: {
    createVideo ({ key, name, size, type }) {
      const data = {
        key,
        title: name,
        description: '',
        file_size: Math.round(size / 1000),
        upload_client: 'v1/multipart',
        vtype: type,
        bucket: S3_BUCKET_NAME,
        user: this.user.id,
        ext: this.extension,
        upload_time: Math.round(this.elapsedTime)
      }

      store.dispatch('createVideo', data)
    },
  },
  computed: {
    user () {
      return store.state.user
    },
  },
  mounted () {
    this.uppy = new Uppy({
      debug: process.env.NODE_ENV === 'development',
      id: 'uppy',
      autoProceed: true,
      restrictions: {
        maxFileSize: false,
        maxNumberOfFiles: 1,
        minNumberOfFiles: 1,
        allowedFileTypes: ['video/*', '.MOV', '.mov', '.mp4', '.mkv', '.MKV', '.MP4', '.avi', '.AVI', '.flv', '.FLV', '.wmv', '.WMV']
      },
      onBeforeUpload: () => {
        // FIXME: I don't think the context is right, but it seems to work
        store.dispatch('createEventCF', {
          'message': 'upload started'
        })
        this.startTime = new Date()
      },
    })
    const headers = () => ({ headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })

    this.uppy
      .use(Dashboard, {
        trigger: '#add-video',
        inline: false,
        showProgressDetails: true,
        hideUploadButton: true,
        proudlyDisplayPoweredByUppy: false,
        locale: {
          strings: {
            selectToUpload: 'Select video to upload',
            dropPasteImport: 'Drag your video file here or select from',
          },
        },
      })
      .use(AwsS3Multipart, {
        serverUrl: '/api/v1',
        createMultipartUpload (file) {
          // NOTE(chdsbd): We need to copy and modify this function so we can pass the auth
          // token to our api. We can remove this if we add cookie authentication.
          // https://github.com/transloadit/uppy/blob/d5de77d03b1e1ffca65a92273c50eace8406f9b5/packages/%40uppy/aws-s3-multipart/src/index.js#L94
          return axios.post('/api/v1/s3/multipart', {
            filename: file.name,
            type: file.type
          }, headers()).then(res => res.data)
        },
        listParts (_file, { key, uploadId }) {
          const filename = encodeURIComponent(key)
          return axios.get(`api/v1/s3/multipart/${uploadId}?key=${filename}`, headers())
            .then(res => res.data)
        },
        prepareUploadPart (_file, { key, uploadId, number }) {
          const filename = encodeURIComponent(key)
          return axios.get(`api/v1/s3/multipart/${uploadId}/${number}?key=${filename}`, headers())
            .then(res => res.data)
        },
        completeMultipartUpload (_file, { key, uploadId, parts }) {
          const filename = encodeURIComponent(key)
          const uploadIdEnc = encodeURIComponent(uploadId)
          return axios.post(`api/v1/s3/multipart/${uploadIdEnc}/complete?key=${filename}`, { parts }, headers())
            .then(res => res.data)
        },
        abortMultipartUpload (_file, { key, uploadId }) {
          const filename = encodeURIComponent(key)
          const uploadIdEnc = encodeURIComponent(uploadId)
          return axios.delete(`api/v1/s3/multipart/${uploadIdEnc}?key=${filename}`, headers())
            .then(res => res.data)
        }

      })
      .use(Webcam, {
        target: Dashboard,
        modes: [
          'video-audio'
        ],
        locale: {
          strings: {
            smile: 'Begin video recording',
            startRecording: 'Begin video recording'
          }
        }
      })
    this.uppy.on('complete', result => {
      this.elapsedTime = (new Date() - this.startTime) / 1000
      console.log(this.elapsedTime)
      console.log('successful files:', result.successful)
      const data = result.successful[0]
      const key = data.s3Multipart.key
      const { name, type, size } = data
      if (key == null || name == null || type == null || size == null) {
        throw Error('Could not find all metadata: key:{key}, name: {name}, type: {type}, size: {size}')
      }
      this.createVideo({ key, name, size, type })
      // FIXME: Need better error handling
      if (result.failed.length > 0) {
        console.log('failed files:', result.failed)
        Raven.captureMessage(`Failed to upload files: \n${JSON.stringify(result.failed, null, 2)}`)
      }
      this.uppy.getPlugin('Dashboard').closeModal()
      this.uppy.reset()
      store.commit('notify', { message: 'Video uploaded successfully', level: 'success' })
    })
    // this.uppy.on('core:upload-progress', (file, progress) => {
    //   // file: { id, name, type, ... }
    //   // progress: { uploader, bytesUploaded, bytesTotal }
    //   console.log(file.id, progress.bytesUploaded, progress.bytesTotal)
    // })
    this.uppy.on('upload-error', (file, error) => {
      store.dispatch('createEventCF', { 'message': `upload error ${file.id}` })
      Raven.captureException(error)
    })
    this.uppy.on('file-added', (file) => {
      store.dispatch('createEventCF', { 'message': `file added ${file.id}` })
    })
  },
  beforeDestroy () {
    // since uppy puts stuff in the body, we need to delete it
    // we could have uppy put stuff inside the vue component but that results
    // in artifacts during the initial render
    this.uppy.close()
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style>
.uppy-DragDrop-inner {
  padding: 40px 80px;
}
</style>

<style scoped>

.UppyDragDrop-Progress {
  position: relative;
}
.UppyDragDrop-Upload {
  display: block;
  margin: auto;
  padding: 5px 15px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  border: 0;
  border: 1px solid gray;
  background: none;
  cursor: pointer;
  margin-top: 15px;
}
.UppyDragDrop-Upload:hover {
  background: gray;
}
.img { max-width: 100px; }
</style>



// WEBPACK FOOTER //
// Uppy.vue?07368175